import React, { useCallback, useContext, useMemo, useState } from "react";

import {
  emitProductGroupImpression,
  emitProductImpression,
  emitProductListInteraction,
  emitRecommenderImpression,
  emitRecommenderRequest,
  FromProduct,
  ProductListInteractionType,
  SOURCE_TYPE_COPURCHASED_PRODUCTS_RECOMMENDER,
} from "@catchoftheday/analytics";
import { AddItemsToCartButton } from "@catchoftheday/cart-components";
import {
  Alert,
  Box,
  Flex,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  LoadingSpinner,
  Span,
  Text,
} from "@catchoftheday/cg-components";
import { CustomerContext } from "@catchoftheday/customer";
import {
  centsToDollars,
  formatPrice,
  getEncodedUrl,
  resizeImage,
  useBreakpoint,
  useIsInViewport,
} from "@catchoftheday/utilities";

import {
  Offer,
  useCoPurchasedProductsQuery,
} from "@personalisation/generated/graphql";

const sourceType = SOURCE_TYPE_COPURCHASED_PRODUCTS_RECOMMENDER;
const sourceId = "product_page_recommender_bi_copurchase_product";

type ProductAssets = {
  hero: {
    url: string;
    altText: string;
  };
};

type SourceProductOffer = Pick<
  Offer,
  "id" | "sellPrice" | "purchasableQuantity"
>;

export type SourceProduct = {
  id: string;
  offer: SourceProductOffer;
  title: string;
  assets: ProductAssets;
};

type CoPurchasedRecommenderProps = {
  product: SourceProduct;
  offerId?: string;
  eventId?: string;
  adult: boolean;
};

type ProductItemProps = Pick<SourceProduct, "assets" | "title"> & {
  currentlyViewing: boolean;
  url: string;
  onClick: () => void;
  offer: SourceProductOffer;
};

const CoPurchasedRecommenderItem = ({
  currentlyViewing,
  url,
  assets,
  title,
  offer,
  onClick,
}: ProductItemProps) => (
  <ListItem mb="16px">
    <Flex alignItems="flex-start">
      <Box border="solid 1px" borderColor="ui.borderColor">
        {currentlyViewing ? (
          <Image
            src={resizeImage(assets.hero.url, "_w200h164")}
            alt={assets.hero.altText || title}
            width="100px"
            height="82px"
          />
        ) : (
          <Link href={url} onClick={onClick}>
            <Image
              src={resizeImage(assets.hero.url, "_w200h164")}
              alt={assets.hero.altText || title}
              width="100px"
              height="82px"
            />
          </Link>
        )}
      </Box>
      <Box pl="16px">
        {currentlyViewing ? (
          <Span
            backgroundColor="ui.borderColor"
            fontSize="xs"
            fontWeight="normal"
            px="8px"
            py="3px"
            borderRadius="3px"
          >
            Currently viewing
          </Span>
        ) : (
          <Link
            fontWeight="light"
            href={url}
            color="text"
            onClick={onClick}
            fontSize="sm"
          >
            {title}
          </Link>
        )}
        <Text fontWeight="normal" fontSize="sm" mt="12px">
          {formatPrice(offer.sellPrice.amount)}
        </Text>
      </Box>
    </Flex>
  </ListItem>
);

export const CoPurchasedRecommender: React.FC<CoPurchasedRecommenderProps> = ({
  product,
  eventId,
  adult,
}) => {
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const { data: customer, loading: customerLoading } =
    useContext(CustomerContext);
  const { isMobile } = useBreakpoint({ minusOnePx: true });
  const isClubMember = customer?.club?.active || false;
  const { id: productId } = product;
  const fromProduct: FromProduct = useMemo(
    () => ({
      from_product_ids: [Number(productId)],
      from_product_offer_id: Number(product.offer.id),
      from_product_displayed_price: centsToDollars(
        product.offer.sellPrice.amount
      ),
    }),
    [productId, product.offer]
  );

  const {
    loading: coPurchasedRecommenderLoading,
    error: coPurchasedRecommenderError,
    data: coPurchasedRecommenderData,
  } = useCoPurchasedProductsQuery({
    variables: {
      productId,
      includeClubProducts: isClubMember,
      adult,
    },
    ssr: false,
    skip: customerLoading,
    onCompleted: ({
      coPurchasedProducts: {
        metaData: {
          preHydrationCount,
          postHydrationCount,
          hydrationErrorCount,
        },
      },
    }) => {
      emitRecommenderRequest({
        source_type: SOURCE_TYPE_COPURCHASED_PRODUCTS_RECOMMENDER,
        pre_hydration_product_count: preHydrationCount,
        post_hydration_product_count: postHydrationCount,
        hydration_error_count: hydrationErrorCount,
        ...fromProduct,
      });
    },
  });

  const recommendedProducts =
    coPurchasedRecommenderData?.coPurchasedProducts.products.map((product) => {
      if (product.bestOffer) {
        return {
          ...product,
          offer: product.bestOffer,
        };
      }
    }) as SourceProduct[];

  const productsToBeAdded = [{ ...product }].concat(recommendedProducts || []);
  const totalProducts = productsToBeAdded.length;
  const totalPrice = productsToBeAdded.reduce((total, product) => {
    return total + product.offer.sellPrice.amount;
  }, 0);

  const addToCartItems = productsToBeAdded.map((product, index) => ({
    title: product.title,
    offerId: product.offer.id,
    quantity: 1,
    imageUrl: product.assets.hero.url,
    sourceId,
    sourceType,
    sourcePosition: index + 1,
    eventId: eventId ? Number(eventId) : undefined,
  }));

  const trackListInteraction = useCallback(
    ({
      product: productItem,
      position,
      interaction,
    }: {
      product: SourceProduct;
      position: number;
      interaction: ProductListInteractionType;
    }) => {
      emitProductListInteraction({
        interaction,
        product_id: Number(productItem.id),
        offer_id: Number(productItem.offer.id),
        source_position: position,
        source_type: sourceType,
        source_id: sourceId,
        ...fromProduct,
      });
    },
    [fromProduct]
  );

  const trackAddToCartInteractions = useCallback(() => {
    productsToBeAdded.forEach((product, index) => {
      trackListInteraction({
        product,
        position: index + 1,
        interaction: "addToCart",
      });
    });
  }, [productsToBeAdded, trackListInteraction]);

  const trackImpressions = useCallback(() => {
    if (!recommendedProducts) {
      return;
    }
    emitProductGroupImpression({
      source_type: sourceType,
      product_count: productsToBeAdded.length,
      displayed_group_price: centsToDollars(totalPrice),
    });
    productsToBeAdded.forEach((productItem, index) => {
      emitProductImpression({
        offer_id: Number(productItem.offer.id),
        source_id: sourceId,
        source_position: index + 1,
        source_type: sourceType,
        product_id: Number(productItem.id),
        product_title: productItem.title,
        displayed_price: centsToDollars(productItem.offer.sellPrice.amount),
        ...fromProduct,
      });
    });
    emitRecommenderImpression({
      source_type: sourceType,
      recommended_product_count: recommendedProducts.length,
      from_product_ids: [Number(productId)],
      from_product_offer_id: Number(product.offer.id),
      from_product_displayed_price: centsToDollars(
        product.offer.sellPrice.amount
      ),
    });
  }, [
    recommendedProducts,
    product,
    productId,
    productsToBeAdded,
    totalPrice,
    fromProduct,
  ]);

  const { ref } = useIsInViewport({
    shouldTrackVisibility: !!recommendedProducts,
    threshold: isMobile ? 0.75 : 1,
    onComponentSeen: trackImpressions,
  });

  if (
    coPurchasedRecommenderLoading ||
    coPurchasedRecommenderError ||
    productsToBeAdded.length <= 1 ||
    !product.offer
  ) {
    return null;
  }

  return (
    <Box
      ref={ref}
      border="solid 1px"
      borderColor="ui.borderColor"
      p="16px"
      mb="16px"
      backgroundColor="white"
      position="relative"
    >
      {loading && <LoadingSpinner hasOverlay />}
      <Heading.h4 mb="16px" mt="0" textAlign={{ xs: "center", md: "left" }}>
        Frequently bought together
      </Heading.h4>
      <List>
        {productsToBeAdded.map((product, index) => {
          return (
            <CoPurchasedRecommenderItem
              key={product.id}
              currentlyViewing={index === 0}
              url={getEncodedUrl(`/product/${product.id}`, {
                sp: (index + 1).toString(),
                st: `${sourceType}`,
                sid: sourceId,
                pid: product.id,
                oid: product.offer.id,
              })}
              assets={product.assets}
              title={product.title}
              offer={product.offer}
              onClick={() =>
                trackListInteraction({
                  product,
                  position: index + 1,
                  interaction: "click",
                })
              }
            />
          );
        })}
      </List>
      {error && (
        <Alert
          type="inline"
          variant="error"
          showCloseButton={false}
          wrapperProps={{ mb: "10px" }}
        >
          <Flex flexWrap="wrap">
            <Box my="2px" mr="4px" fontSize="inherit" fontWeight="normal">
              Something went wrong. Please try again.
            </Box>
          </Flex>
        </Alert>
      )}
      {product.offer.purchasableQuantity > 0 && (
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" mt="12px">
            Total:{" "}
            <Span fontSize="base" fontWeight="normal">
              {formatPrice(totalPrice)}
            </Span>
          </Text>
          <AddItemsToCartButton
            items={addToCartItems}
            setError={setError}
            setLoading={setLoading}
            onClick={trackAddToCartInteractions}
            variant="secondary"
            outline
            size="sm"
          >
            Add {totalProducts} items to cart
          </AddItemsToCartButton>
        </Flex>
      )}
    </Box>
  );
};
